import { supabase } from "./supabase";

/**
 * Detect the set of columns present in a Supabase table.
 * Strategy: fetch 1 row and inspect Object.keys. Result is cached in-memory
 * per table so we don't refetch on every save.
 */
const columnCache = new Map();
// Columns we've *learned* are missing (from PostgREST errors) — even if the
// table is empty. Merged into detectColumns so subsequent saves skip them.
const knownMissing = new Map(); // table -> Set<string>

export async function detectColumns(table) {
  if (columnCache.has(table)) return columnCache.get(table);
  const { data } = await supabase.from(table).select("*").limit(1);
  const cols = new Set(Array.isArray(data) && data[0] ? Object.keys(data[0]) : []);
  columnCache.set(table, cols);
  return cols;
}

export function filterToColumns(payload, allowed) {
  if (!allowed || allowed.size === 0) return payload;
  const out = {};
  for (const [k, v] of Object.entries(payload)) {
    if (allowed.has(k)) out[k] = v;
  }
  return out;
}

export function resetColumnCache(table) {
  if (table) {
    columnCache.delete(table);
    knownMissing.delete(table);
  } else {
    columnCache.clear();
    knownMissing.clear();
  }
}

/**
 * Extract the offending column name from a Supabase PostgREST error thrown
 * when a payload contains a column that doesn't exist. Handles the
 * variants PostgREST emits, including:
 *   • "Could not find the 'category' column of 'blogs' in the schema cache"
 *   • "column \"category\" of relation \"blogs\" does not exist"
 */
function extractMissingColumn(err) {
  if (!err) return null;
  const msg = `${err.message || ""} ${err.details || ""} ${err.hint || ""}`;
  // PostgREST v11+: Could not find the 'colname' column
  let m = msg.match(/Could not find the ['"`]([^'"`]+)['"`] column/i);
  if (m) return m[1];
  // Postgres: column "colname" of relation "..." does not exist
  m = msg.match(/column ["'`]?([a-zA-Z0-9_]+)["'`]? of relation/i);
  if (m) return m[1];
  return null;
}

function stripMissing(table, payload) {
  const missing = knownMissing.get(table);
  if (!missing || missing.size === 0) return payload;
  const out = {};
  for (const [k, v] of Object.entries(payload)) {
    if (!missing.has(k)) out[k] = v;
  }
  return out;
}

function rememberMissing(table, column) {
  if (!column) return;
  if (!knownMissing.has(table)) knownMissing.set(table, new Set());
  knownMissing.get(table).add(column);
  // Also drop it from the positive-cache if it was there
  const cols = columnCache.get(table);
  if (cols) cols.delete(column);
}

/**
 * Self-healing insert: if PostgREST rejects the payload because a column is
 * missing, remove that column and retry. Repeats until success or a
 * non-schema error is hit. Guards against runaway loops.
 */
export async function safeInsert(table, payload, { select = "*", single = true } = {}) {
  let body = stripMissing(table, payload);
  for (let i = 0; i < 20; i++) {
    let q = supabase.from(table).insert(body).select(select);
    if (single) q = q.single();
    const res = await q;
    if (!res.error) return res;
    const missing = extractMissingColumn(res.error);
    if (!missing || !(missing in body)) return res; // unrelated error
    rememberMissing(table, missing);
    delete body[missing];
    if (Object.keys(body).length === 0) return res;
  }
  return { data: null, error: new Error("Too many schema retries") };
}

/**
 * Self-healing update. `match` is an object of column: value equality filters.
 */
export async function safeUpdate(table, payload, match, { select = "*", single = true } = {}) {
  let body = stripMissing(table, payload);
  for (let i = 0; i < 20; i++) {
    let q = supabase.from(table).update(body);
    for (const [col, val] of Object.entries(match || {})) q = q.eq(col, val);
    q = q.select(select);
    if (single) q = q.single();
    const res = await q;
    if (!res.error) return res;
    const missing = extractMissingColumn(res.error);
    if (!missing || !(missing in body)) return res;
    rememberMissing(table, missing);
    delete body[missing];
    if (Object.keys(body).length === 0) return res;
  }
  return { data: null, error: new Error("Too many schema retries") };
}

/**
 * Upsert convenience — chooses insert vs update based on presence of an id.
 */
export async function safeUpsert(table, payload, idField = "id") {
  const id = payload[idField];
  const body = { ...payload };
  delete body[idField];
  if (id) return safeUpdate(table, body, { [idField]: id });
  return safeInsert(table, body);
}
